import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Search, X, Clock, Calendar as CalendarIcon } from 'lucide-react'
import { useState, useEffect } from 'react'
import { curationService } from '@/services/curation'
import { useNavigate } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { useCuration } from '@/contexts/curation'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { ptBR } from 'react-day-picker/locale'

interface CurationHeaderProps {
  activeTab: string
}

export function CurationHeader({ activeTab }: CurationHeaderProps) {
  const navigate = useNavigate()
  const { filters, setFilters } = useCuration()
  const [localSearchValue, setLocalSearchValue] = useState(filters.search || '')
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date())
  const [periodType, setPeriodType] = useState<'today' | 'week' | 'month' | 'custom'>('today')
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [tempDate, setTempDate] = useState<Date | undefined>(new Date())

  useEffect(() => {
    setSelectedDate(new Date())
    setTempDate(new Date())
  }, [])

  useEffect(() => {
    if (calendarOpen) {
      setTempDate(selectedDate)
    }
  }, [calendarOpen, selectedDate])
  
  const tabs = [
    { id: 'boards', label: 'Murais', path: 'boards' },
    { id: 'metrics', label: 'Métricas', path: 'metrics' }
  ]

  const handleTabChange = (path: string) => {
    navigate(path)
  }

  useEffect(() => {
    setLocalSearchValue(filters.search || '')
  }, [filters.search])

  const handleSearchClick = () => {
    setFilters({ ...filters, search: localSearchValue })
  }

  const handleClearSearch = () => {
    setLocalSearchValue('')
    setFilters({ ...filters, search: '' })
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setFilters({ ...filters, search: localSearchValue })
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">Curadoria</h1>
        <p className="text-gray-600">Confira os conteúdos curados</p>
      </div>
      <hr className="border-gray-200" />
      <div className="flex items-center justify-between">
        <div className="flex space-x-1 bg-white p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.path)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'text-gray-900'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{
                backgroundColor: activeTab === tab.id ? '#F5F5F5' : 'transparent'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <hr className="border-gray-200" />

      {activeTab === 'boards' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Input
                placeholder="Pesquisar"
                className="pl-10 pr-10 w-64"
                iconLeft={<Search className="h-4 w-4 stroke-muted-foreground" />}
                value={localSearchValue}
                onChange={(e) => setLocalSearchValue(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              {localSearchValue && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button 
              className="bg-gray-900 hover:bg-gray-800 text-white px-6"
              onClick={handleSearchClick}
            >
              OK
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'boards' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="text-xl font-semibold">Em Alta Hoje</div>
                <div className="text-gray-600 mt-1">Veja suas oportunidades do dia!</div>
              </div>
              <Select defaultValue="contexts">
                <SelectTrigger className="w-[140px] h-9 bg-white border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contexts">Contextos</SelectItem>
                  <SelectItem value="trending-topics">Trending Topics</SelectItem>
                  <SelectItem value="creators">Creators</SelectItem>
                  <SelectItem value="trends">Trends</SelectItem>
                  <SelectItem value="formatos">Formatos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <TrendingList />
          </div>
          <div className="border rounded-xl p-6 flex flex-col justify-center" style={{ backgroundColor: '#F5F5F5' }}>
            <div className="text-xl font-semibold">Período do Mural</div>
            <div className="text-gray-600 mt-1">Escolha qual data gostaria de exibir seus conteúdos pinados.</div>
            <div className="mt-4 w-full max-w-md flex items-center gap-2">
              <Select value={periodType} onValueChange={(value) => setPeriodType(value as 'today' | 'week' | 'month' | 'custom')}>
                <SelectTrigger className="flex-1 bg-white border-gray-200 min-w-[200px] max-w-[300px]">
                  <div className="flex items-center gap-2 flex-1">
                    <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <SelectValue placeholder="Selecione o Período" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="week">Essa Semana</SelectItem>
                  <SelectItem value="month">Esse Mês</SelectItem>
                  <SelectItem value="custom">Período Personalizado</SelectItem>
                </SelectContent>
              </Select>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-10 w-10 p-0 bg-white border-gray-200 flex items-center justify-center"
                  >
                    <CalendarIcon className="h-4 w-4 text-gray-900" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={tempDate}
                    onSelect={(date) => {
                      setTempDate(date)
                    }}
                    locale={ptBR}
                  />
                  <div className="flex justify-center p-3">
                    <Button
                      onClick={() => {
                        setSelectedDate(tempDate)
                        setPeriodType('custom')
                        setCalendarOpen(false)
                      }}
                      className="bg-black hover:bg-gray-800 text-white px-6"
                    >
                      OK
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function TrendingList() {
  const [items, setItems] = useState<Array<{ name: string; posts: number }>>([])
  const [loading, setLoading] = useState(true)
  const { filters } = useCuration()

  useEffect(() => {
    let mounted = true
    setLoading(true)
    curationService.getTrending().then((res) => {
      if (!mounted) return
      if (Array.isArray(res) && res.length > 0) {
        const mapped = res.map((r) => ({ name: r.context?.name || 'Contexto', posts: r.postCount || 0 }))
        setItems(mapped)
      } else {
        setItems([])
      }
    }).catch(() => {
      setItems([])
    }).finally(() => {
      if (mounted) setLoading(false)
    })
    return () => { mounted = false }
  }, [])

  const displayItems = items.length >= 4 
    ? items.slice(0, 4)
    : [...items, ...Array.from({ length: 4 - items.length }).map((_) => ({ name: '', posts: 0, isEmpty: true }))]
  const hasFilters = filters.territories || filters.topics

  if (loading) {
    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="space-y-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-4 w-20" />
              <div className="flex items-center gap-3">
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-4 h-4" />
                <Skeleton className="w-4 h-4" />
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {displayItems.map((it: any, idx) => {
          if (it.isEmpty) {
            return <div key={`empty-${idx}`} className="space-y-2" />
          }
          return (
            <div key={`${it.name}-${idx}`} className="space-y-2">
              <Badge className="bg-white border-gray-200 text-gray-900" variant="secondary">{it.name}</Badge>
              <div className="text-gray-600">{it.posts.toLocaleString('pt-BR')} posts</div>
              <div className="flex items-center gap-3 text-gray-800">
              </div>
            </div>
          )
        })}
      </div>
      {hasFilters && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="text-sm font-medium text-gray-700 mb-2">Filtros Aplicados:</div>
          <div className="flex flex-wrap gap-2">
            {filters.territories && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Territórios:</span> {filters.territories}
              </div>
            )}
            {filters.topics && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Formatos:</span> {filters.topics}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
